import type { FC } from "react"
import React from "react"

import { AnimatePresence } from "framer-motion"
import { Helmet } from "react-helmet-async"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import styled from "styled-components"

import EnvVars from "~/env-vars"
import { Permission } from "~/graphql/client"
import { withAuth } from "~/utils/withAuth"

import Inspector from "./components/Inspector"
import { EnfinErrorModal } from "./containers/Proposal/Brochure/FinancialSummarySlide/credit-application-modal/Enfin/EnfinErrorModal"
import { QueryErrorModal } from "./containers/Proposal/state/notifications/QueryErrorModal"
import { useAuth } from "./hooks/useAuth"
import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics"
import { useRedirectUrl } from "./hooks/useRedirectUrl"

const AdaptiveDesigner = React.lazy(
  () => import(`./containers/AdaptiveDesigner/AdaptiveDesigner`),
)
const ActiveStates = React.lazy(
  () => import(`./containers/Admin/ActiveStates/ActiveStates`),
)
const ApiKeys = React.lazy(
  () => import(`./containers/Admin/ApiKeys/AdminApiKeys`),
)
const IntegrationDetails = React.lazy(
  () => import(`./containers/Admin/ApiKeys/IntegrationDetails`),
)
const NewServiceIntegrationForm = React.lazy(
  () => import(`./containers/Admin/ApiKeys/NewIntegrationForm`),
)
const CreateDev = React.lazy(
  () =>
    import(`./containers/Admin/Proposals/create-proposal/CreateProposalForm`),
)
const CreateBatteryProposalForm = React.lazy(
  () =>
    import(
      `./containers/Admin/Proposals/create-battery-proposal/CreateBatteryProposalForm`
    ),
)
const Equipment = React.lazy(
  () => import(`./containers/Admin/Equipment/AdminEquipment`),
)
const Financing = React.lazy(
  () => import(`./containers/Admin/Financing/AdminFinancing`),
)
const EditFinancePartnerPage = React.lazy(
  () => import(`./containers/Admin/Financing/EditFinancePartnerPage`),
)
const Incentives = React.lazy(
  () => import(`./containers/Admin/Incentives/AdminIncentives`),
)
const CreateIncentive = React.lazy(
  () => import(`./containers/Admin/Incentives/CreateIncentive`),
)
const CreateVppProgram = React.lazy(
  () => import(`./containers/Admin/Incentives/CreateVppProgram`),
)
const EditVppProgram = React.lazy(
  () => import(`./containers/Admin/Incentives/EditVppProgram`),
)
const EditIncentive = React.lazy(
  () => import(`./containers/Admin/Incentives/EditIncentive`),
)
const Proposals = React.lazy(
  () => import(`./containers/Admin/Proposals/Proposals`),
)
const PricingHistory = React.lazy(
  () => import(`./containers/Admin/Proposals/PricingHistory/PricingHistory`),
)
const Reports = React.lazy(() => import(`./containers/Admin/Reports/Reports`))
const ProjectTrendsPage = React.lazy(
  () => import(`./containers/Admin/Reports/project-trends/ProjectTrendsPage`),
)
const ExternalProviderCallsPage = React.lazy(() =>
  import(`./containers/Admin/Reports/Reports`).then((module) => ({
    default: module.ExternalProviderCallsPage,
  })),
)
const BatteryOverviewPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryOverviewPage`),
)
const BatteryAttentionPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryAttentionPage`),
)
const BatteryFinancingPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryFinancingPage`),
)
const BatteryPricingPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryPricingPage`),
)
const BatteryProvidersPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryProvidersPage`),
)
const BatteryEquipmentPage = React.lazy(
  () => import(`./containers/Admin/Reports/battery/BatteryEquipmentPage`),
)
const AiComputeOverviewPage = React.lazy(
  () => import(`./containers/Admin/Reports/ai-compute/AiComputeOverviewPage`),
)
const Users = React.lazy(() => import(`./containers/Admin/Users/AdminUsers`))
const EditUserForm = React.lazy(
  () => import(`./containers/Admin/Users/EditUserForm`),
)
const RepPrograms = React.lazy(
  () => import(`./containers/Admin/RepPrograms/RepPrograms`),
)
const ContractTabLabels = React.lazy(
  () => import(`./containers/Admin/ContractTabLabels/ContractTabLabels`),
)
const AdminIndex = React.lazy(() => import(`./containers/Admin/AdminHome`))
const AdminLayout = React.lazy(() => import(`./containers/Admin/AdminLayout`))

const DevLayout = import.meta.env.DEV
  ? React.lazy(() => import(`./containers/Dev/DevLayout`))
  : null
const DesignSystemPage = import.meta.env.DEV
  ? React.lazy(() => import(`./containers/Dev/DesignSystem/DesignSystemPage`))
  : null

const Designer = React.lazy(() => import(`./containers/LegacyDesigner`))
const ServiceQueueDesigner = React.lazy(
  () => import(`./containers/LegacyDesigner/ServiceQueueDesigner`),
)
const Brochure = React.lazy(
  () => import(`./containers/Proposal/Brochure/Brochure`),
)
const ProposalList = React.lazy(
  () => import(`./containers/Proposal/ProposalHome`),
)
const ProposalView = React.lazy(
  () => import(`./containers/Proposal/ProposalView`),
)
const SolarExplorer = React.lazy(
  () => import(`./containers/SolarExplorer/SolarExplorer`),
)
const SummaryLayout = React.lazy(
  () => import(`./containers/Proposal/Summary/Summary`),
)

const AppContainer = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
`

const ServiceQueueDesignerAuth = withAuth(ServiceQueueDesigner, [
  Permission.vision_admin,
  Permission.admin,
  Permission.vision_designer_user,
  Permission.vision_service_bureau_admin,
  Permission.vision_service_bureau_user,
])

const App: FC = () => {
  const user = useAuth()
  useRedirectUrl(user)

  useGoogleAnalytics()

  return user.id ? (
    <AppContainer>
      <Routes>
        <Route path="/" element={<Navigate replace to="proposal" />} />

        <Route
          path="proposal/:slug/design-next"
          element={<AdaptiveDesigner />}
        />

        <Route path="proposal/*" element={<ProposalLayout />}>
          <Route index element={<ProposalList />} />
          <Route path=":slug/*" element={<ProposalView />}>
            <Route path="*" element={<Brochure />} />
            <Route path="design" element={<Designer />} />
            {/* <Route path="design-next" element={<AdaptiveDesigner />} /> */}
          </Route>
        </Route>

        <Route path="solar-explorer/*" element={<SolarExplorer />} />

        <Route path="summary/:slug/*" element={<SummaryLayout />} />

        <Route path="presentation/" element={<ProposalLayout />}>
          <Route path=":slug/*" element={<ProposalView />}>
            <Route path="*" element={<Brochure />} />
          </Route>
        </Route>

        <Route
          path="designer/service-queue/:designRequestId/"
          element={<ServiceQueueDesignerAuth />}
        />

        <Route path="admin/*" element={<AdminLayout />}>
          <Route index element={<AdminIndex />} />
          <Route path="active-states" element={<ActiveStates />} />
          <Route path="api-keys" element={<ApiKeys />}>
            <Route path="new" element={<NewServiceIntegrationForm />} />
            <Route path=":id" element={<IntegrationDetails />} />
          </Route>
          <Route path="equipment" element={<Equipment />} />
          <Route path="financing" element={<Financing />} />
          <Route
            path="financing/:id/edit"
            element={<EditFinancePartnerPage />}
          />
          <Route path="proposals">
            <Route index element={<Proposals />} />
            {EnvVars.ADMIN_PROPOSAL_CREATE_IS_ENABLED === `true` && (
              <Route path="create" element={<CreateDev />} />
            )}
            <Route
              path="create-battery"
              element={<CreateBatteryProposalForm />}
            />
            {EnvVars.ADMIN_PRICING_HISTORY_ENABLED && (
              <Route
                path="pricing-history/:proposalId?"
                element={<PricingHistory />}
              />
            )}
          </Route>
          <Route path="rep-programs" element={<RepPrograms />} />
          <Route path="contract-tab-labels" element={<ContractTabLabels />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/project-trends" element={<ProjectTrendsPage />} />
          <Route
            path="reports/external-provider-calls"
            element={<ExternalProviderCallsPage />}
          />
          <Route
            path="reports/battery-overview"
            element={<BatteryOverviewPage />}
          />
          <Route
            path="reports/battery-attention"
            element={<BatteryAttentionPage />}
          />
          <Route
            path="reports/battery-financing"
            element={<BatteryFinancingPage />}
          />
          <Route
            path="reports/battery-pricing"
            element={<BatteryPricingPage />}
          />
          <Route
            path="reports/battery-providers"
            element={<BatteryProvidersPage />}
          />
          <Route
            path="reports/battery-equipment"
            element={<BatteryEquipmentPage />}
          />
          <Route
            path="reports/ai-compute-overview"
            element={<AiComputeOverviewPage />}
          />
          <Route path="incentives" element={<Incentives />} />
          <Route path="incentives/new" element={<CreateIncentive />} />
          <Route path="incentives/create" element={<CreateVppProgram />} />
          <Route
            path="incentives/edit/:programId"
            element={<EditVppProgram />}
          />
          <Route
            path="incentives/:incentiveId/edit"
            element={<EditIncentive />}
          />
          <Route path="users" element={<Users />}>
            <Route path=":id" element={<EditUserForm />} />
          </Route>
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
        {import.meta.env.DEV && DevLayout && DesignSystemPage ? (
          <Route path="dev" element={<DevLayout />}>
            <Route path="design-system" element={<DesignSystemPage />} />
          </Route>
        ) : null}
      </Routes>
      <QueryErrorModal />
      <EnfinErrorModal />
      <Inspector />
    </AppContainer>
  ) : (
    <Routes>
      <Route path="/" element={<Navigate replace to="/proposal" />} />
      <Route
        path="*"
        element={
          <div className="flex h-screen w-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          </div>
        }
      />
    </Routes>
  )
}

function ProposalLayout() {
  return (
    <>
      <Helmet>
        <title>Powur Vision: Proposal</title>
      </Helmet>
      <AnimatePresence>
        <Outlet />
      </AnimatePresence>
    </>
  )
}

export default App
